import {
  ActionIcon, Anchor, Box, Button, Group, Menu, Select, Stack, Switch, Text, TextInput, Tooltip,
} from '@mantine/core'
import { DateTimePicker } from '@mantine/dates'
import {
  IconCircleCheck, IconClockPause, IconDots, IconFlag, IconLock, IconRefresh, IconSearch,
  IconTag, IconUserPlus, IconUsersGroup,
} from '@tabler/icons-react'
import { OPCOES_DO_TIPO_DE_CHAMADO } from '../tipoDeChamado'
import type { AcoesDoChamado } from './useAcoesDoChamado'

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Baixa' },
  { value: 'medium', label: 'Média' },
  { value: 'high', label: 'Alta' },
  { value: 'critical', label: 'Crítica' },
]

/** Uma ação da esteira de ambientes (aprovar, executar, validar, aprovar execução, promover). */
export interface AcaoDaEsteira {
  label: string
  onClick: () => void
  ocupado: boolean
}

interface Etapa {
  chave: string
  label: string
  icon?: React.ReactNode
  onClick: () => void
  loading?: boolean
  disabled?: boolean
}

/**
 * Os botões de etapa na ordem do caminho do chamado (spec 2026-10-21 §3.4): a triagem, as
 * transições de status, a esteira e, por último, Finalizar atendimento.
 */
export function etapasDaFaixa(acoes: AcoesDoChamado | null, esteira: AcaoDaEsteira[]): Etapa[] {
  const etapas: Etapa[] = []
  if (acoes?.pode.iniciarTriagem) {
    const m = acoes.iniciarTriagemMutation
    etapas.push({
      chave: 'iniciar-triagem', label: 'Iniciar Triagem', icon: <IconSearch size={14} />,
      onClick: () => m.mutate(), loading: m.isPending,
    })
  }
  if (acoes?.pode.concluirTriagem) {
    const m = acoes.concluirTriagemMutation
    etapas.push({
      chave: 'concluir-triagem', label: 'Concluir triagem', icon: <IconCircleCheck size={14} />,
      onClick: () => m.mutate(false), loading: m.isPending && m.variables === false, disabled: m.isPending,
    })
    etapas.push({
      chave: 'concluir-e-atribuir', label: 'Concluir e atribuir a mim', icon: <IconUserPlus size={14} />,
      onClick: () => m.mutate(true), loading: m.isPending && m.variables === true, disabled: m.isPending,
    })
  }
  if (acoes) {
    const { statusMutation, transicao } = acoes
    for (const t of acoes.transitions) {
      etapas.push({
        chave: `status-${t.to}`, label: t.label, icon: t.icon,
        onClick: () => transicao.escolher(t.to),
        loading: statusMutation.isPending && transicao.pendente === t.to,
        disabled: statusMutation.isPending && transicao.pendente !== t.to,
      })
    }
  }
  esteira.forEach((a, i) => etapas.push({
    chave: `esteira-${i}`, label: a.label, onClick: a.onClick, loading: a.ocupado,
  }))
  if (acoes?.pode.finalizar) {
    const m = acoes.finalizeMutation
    etapas.push({
      chave: 'finalizar', label: 'Finalizar atendimento', icon: <IconCircleCheck size={14} />,
      onClick: () => m.mutate(), loading: m.isPending,
    })
  }
  return etapas
}

/** A faixa tem o que mostrar: uma etapa, o seletor de atendente ou um formulário aberto. */
export function faixaTemConteudo(acoes: AcoesDoChamado | null, esteira: AcaoDaEsteira[]): boolean {
  if (etapasDaFaixa(acoes, esteira).length > 0) return true
  if (!acoes) return false
  return acoes.pode.seletor || acoes.transicao.pendente != null
    || acoes.formulario.ativo != null || acoes.formulario.reiniciar.aberto
}

interface FaixaProximoPassoProps {
  acoes: AcoesDoChamado | null
  esteira?: AcaoDaEsteira[]
}

/**
 * A faixa "Próximo passo" do cartão de leitura (spec 2026-10-21 §3.4). O primeiro botão é o
 * próximo passo esperado e leva a variante Primary (o gradiente vem do tema); os irmãos são
 * Secondary. Nenhuma ação de etapa tem cor própria (DS §6.1).
 */
export function FaixaProximoPasso({ acoes, esteira = [] }: FaixaProximoPassoProps) {
  if (!faixaTemConteudo(acoes, esteira)) return null
  const etapas = etapasDaFaixa(acoes, esteira)

  return (
    <Box
      data-testid="faixa-proximo-passo"
      style={{
        backgroundColor: 'var(--ai-body-bg)',
        borderTop: '1px solid var(--ai-border-light)',
        padding: '12px 16px',
      }}
    >
      <Stack gap="sm">
        <Text size="xs" fw={600} c="var(--ai-text-secondary)" tt="uppercase">Próximo passo</Text>
        {etapas.length > 0 && (
          <Group gap="xs">
            {etapas.map((e, i) => (
              <Button
                key={e.chave}
                variant={i === 0 ? undefined : 'outline'}
                leftSection={e.icon}
                onClick={e.onClick}
                loading={e.loading}
                disabled={e.disabled}
              >
                {e.label}
              </Button>
            ))}
          </Group>
        )}
        {acoes?.pode.seletor && <SeletorDeAtendente acoes={acoes} />}
        {acoes && <Formularios acoes={acoes} />}
      </Stack>
    </Box>
  )
}

function SeletorDeAtendente({ acoes }: { acoes: AcoesDoChamado }) {
  const { seletor, chamado, isAdmin } = acoes
  const { assignableData, loadingAssignable, userOptions, includeOutsideTeam } = seletor
  return (
    <Stack gap={6}>
      <Group gap="md" align="flex-end" wrap="wrap">
        <Select
          size="xs"
          w={{ base: '100%', xs: 320 }}
          label={
            assignableData?.target_template_name
              ? `Atendente — ${assignableData.target_template_name}${includeOutsideTeam ? ' + fora do time' : ''}`
              : 'Atendente'
          }
          placeholder={
            loadingAssignable
              ? 'Carregando...'
              : userOptions.length === 0
                ? 'Nenhum atendente disponível'
                : 'Selecionar atendente'
          }
          searchable
          clearable
          data={userOptions}
          value={chamado.assignee ? String(chamado.assignee.id) : null}
          onChange={seletor.atribuir}
          disabled={seletor.atribuindo || loadingAssignable}
          leftSection={<IconUserPlus size={14} />}
          nothingFoundMessage="Nenhum usuário encontrado"
        />
        {/* Toggle admin — Modelo A com override */}
        {isAdmin && (
          <Switch
            size="xs"
            mb={6}
            label="Mostrar usuários fora do time (admin)"
            checked={includeOutsideTeam}
            onChange={(e) => seletor.setIncludeOutsideTeam(e.currentTarget.checked)}
            thumbIcon={includeOutsideTeam ? <IconUsersGroup size={10} /> : null}
          />
        )}
      </Group>
      {!loadingAssignable && userOptions.length === 0 && (
        <Text size="xs" c="var(--ai-text-secondary)">
          {assignableData?.target_template_id == null ? (
            <>
              Este chamado não tem time destinatário.{' '}
              <Anchor size="xs" component="a" href="#" onClick={(e) => e.preventDefault()}>
                Encaminhe pra um time
              </Anchor>{' '}
              para listar atendentes.
            </>
          ) : (
            <>
              Nenhum atendente no time
              {assignableData?.target_template_name ? ` "${assignableData.target_template_name}"` : ''}
              {' '}para este tenant.
            </>
          )}
        </Text>
      )}
    </Stack>
  )
}

function Cancelar({ onClick }: { onClick: () => void }) {
  return <Button size="xs" variant="subtle" color="gray" onClick={onClick}>Cancelar</Button>
}

/** Os formulários em linha que as ações abrem: a nota da transição, Reclassificar, Bloquear e Reiniciar. */
function Formularios({ acoes }: { acoes: AcoesDoChamado }) {
  const { transicao, statusMutation, formulario } = acoes
  const { reclassificar: rc, bloquear: bl, reiniciar: rf } = formulario
  const transicaoDestrutiva = transicao.pendente === 'closed'
  // O admin reabre com motivo (TASK-11941): o backend recusa a reabertura dele sem nota.
  const reabertura = transicao.pendente === 'reopened'

  return (
    <>
      {transicao.pendente && (
        <Stack gap="xs" maw={480}>
          <TextInput
            size="xs"
            label={transicaoDestrutiva ? 'Fechar o chamado' : reabertura ? 'Reabrir o chamado' : undefined}
            placeholder={reabertura ? 'Motivo (obrigatório)' : 'Nota opcional...'}
            value={transicao.note}
            onChange={(e) => transicao.setNote(e.currentTarget.value)}
          />
          <Group gap="xs">
            <Button
              size="xs"
              color={transicaoDestrutiva ? 'red' : undefined}
              onClick={() => statusMutation.mutate(transicao.pendente!)}
              loading={statusMutation.isPending}
              disabled={reabertura && !transicao.note.trim()}
            >
              Confirmar
            </Button>
            <Cancelar onClick={transicao.cancelar} />
          </Group>
        </Stack>
      )}

      {formulario.ativo === 'reclassify' && (
        <Stack gap="xs" maw={480}>
          <Select
            size="xs" label="Tipo" placeholder="Selecionar tipo"
            data={OPCOES_DO_TIPO_DE_CHAMADO} value={rc.reRequestType} onChange={rc.setReRequestType} clearable
          />
          <Select
            size="xs" label="Categoria" placeholder="Selecionar categoria" searchable clearable
            data={rc.categoryOptions} value={rc.reCategory} onChange={rc.setReCategory}
            nothingFoundMessage="Nenhuma categoria"
          />
          <Select
            size="xs" label="Prioridade" placeholder="Selecionar prioridade"
            data={PRIORITY_OPTIONS} value={rc.rePriority} onChange={rc.setRePriority} clearable
          />
          <TextInput
            size="xs" placeholder="Nota (opcional)" value={rc.reNote}
            onChange={(e) => rc.setReNote(e.currentTarget.value)}
          />
          <Group gap="xs">
            <Button
              size="xs" onClick={() => rc.mutation.mutate()} loading={rc.mutation.isPending}
              disabled={!rc.reCategory && !rc.rePriority && !rc.reRequestType}
            >
              Aplicar
            </Button>
            <Cancelar onClick={formulario.fechar} />
          </Group>
        </Stack>
      )}

      {formulario.ativo === 'block' && (
        <Stack gap="xs" maw={480}>
          <DateTimePicker
            size="xs" label="Bloquear até" placeholder="Selecionar data/hora"
            value={bl.blockedUntil} onChange={bl.setBlockedUntil}
            minDate={new Date()} clearable
            leftSection={<IconFlag size={14} />}
          />
          <TextInput
            size="xs" placeholder="Nota (opcional)" value={bl.blockNote}
            onChange={(e) => bl.setBlockNote(e.currentTarget.value)}
          />
          <Group gap="xs">
            <Button
              size="xs" onClick={() => bl.mutation.mutate()} loading={bl.mutation.isPending}
              disabled={!bl.blockedUntil}
            >
              Bloquear
            </Button>
            <Cancelar onClick={formulario.fechar} />
          </Group>
        </Stack>
      )}

      {rf.aberto && (
        <Stack gap="xs" maw={480}>
          <Text size="xs" c="var(--ai-text-secondary)">
            Volta para a triagem no ambiente em que o chamado nasceu. Execução, validação e
            aceite são zerados; o histórico é mantido.
          </Text>
          <TextInput
            size="xs"
            placeholder="Motivo (obrigatório)"
            value={rf.restartReason}
            onChange={(e) => rf.setRestartReason(e.currentTarget.value)}
          />
          <Group gap="xs">
            <Button
              size="xs" onClick={() => rf.mutation.mutate()}
              loading={rf.mutation.isPending} disabled={!rf.restartReason.trim()}
            >
              Confirmar reinício
            </Button>
            <Cancelar onClick={rf.fechar} />
          </Group>
        </Stack>
      )}
    </>
  )
}

/**
 * O menu Mais do rodapé (spec 2026-10-21 §3.5): Reclassificar, Bloquear, Reiniciar fluxo e
 * Reabrir, cada um só para quem podia antes. Cada item abre o seu formulário na faixa.
 */
export function MenuMaisAcoes({ acoes }: { acoes: AcoesDoChamado }) {
  const { pode, formulario, transicao } = acoes
  if (!pode.reclassificar && !pode.bloquear && !pode.reiniciar && !pode.reabrir) return null
  return (
    <Menu position="top-end" withinPortal>
      <Menu.Target>
        <Tooltip label="Mais ações">
          <ActionIcon variant="outline" size={36} aria-label="Mais ações">
            <IconDots size={16} />
          </ActionIcon>
        </Tooltip>
      </Menu.Target>
      <Menu.Dropdown>
        {pode.reclassificar && (
          <Menu.Item leftSection={<IconTag size={14} />} onClick={() => formulario.abrir('reclassify')}>
            Reclassificar
          </Menu.Item>
        )}
        {pode.bloquear && (
          <Menu.Item leftSection={<IconClockPause size={14} />} onClick={() => formulario.abrir('block')}>
            Bloquear
          </Menu.Item>
        )}
        {pode.reiniciar && (
          <Menu.Item leftSection={<IconRefresh size={14} />} onClick={() => formulario.abrir('restart')}>
            Reiniciar fluxo
          </Menu.Item>
        )}
        {pode.reabrir && (
          <Menu.Item
            leftSection={<IconRefresh size={14} />}
            onClick={() => transicao.escolher('reopened')}
          >
            Reabrir
          </Menu.Item>
        )}
      </Menu.Dropdown>
    </Menu>
  )
}

/** "Fechar" do admin (o antigo "Fechar (Admin)"): Danger, e a confirmação abre na faixa. */
export function BotaoFechar({ acoes }: { acoes: AcoesDoChamado }) {
  if (!acoes.pode.fechar) return null
  const { statusMutation, transicao } = acoes
  return (
    <Button
      color="red"
      variant="outline"
      leftSection={<IconLock size={14} />}
      onClick={() => transicao.escolher('closed')}
      loading={statusMutation.isPending && transicao.pendente === 'closed'}
    >
      Fechar
    </Button>
  )
}
